import Service, { service } from '@ember/service'
import quoteConfig from 'customer-flows/quote/config'
import {
  type ModelKey,
  type ModelKeyWithRefid,
  type QuoteModel,
  type BuilderData,
  type QuoteType,
  type SchemaNode
} from 'customer-flows/utils/quote/models'

import type { Resolver, ResolverKey, TraitList } from 'customer-flows/utils/quote/resolvers'
import { resolveStoreProp as upstreamResolveStoreProp } from 'customer-flows/utils/core'
import hash from 'hash-it'
import { command, context } from 'customer-flows/utils/core'
import Logger from 'customer-flows/utils/logger'
import uid from 'customer-flows/utils/uid'

const DEFAULT_QUOTE_TYPE: QuoteType = 'strict'

let refids: { [key: string]: string } = {}

export function getRefid(field: string) {
  return refids[field]
}

export function resetRefids() {
  refids = {}
}

/**
 * Builder class for constructing and committing Quote objects
 * in either 'strict' or 'sparse' formats. Supports partial application,
 * validation, quote logging, and dirty-state detection.
 */
export default class QuoteBuilderService<
  Mode extends QuoteType = typeof DEFAULT_QUOTE_TYPE,
  IncludedKeys extends ResolverKey = never
> extends Service {
  private _data: BuilderData = {}
  private _input: QuoteSparseInput = {}
  private _quoteType: QuoteType = DEFAULT_QUOTE_TYPE
  private _isValid: boolean = false

  private _previousHash: number | undefined = undefined
  private _pending: boolean = false
  private _queuedInput: QuoteSparseInput | undefined = undefined
  private _log: QuoteSparseInput[] = []

  @service flowManager: FlowManager
  @command('quote') createOrUpdateQuote: QuoteCommands['createOrUpdateQuote']
  @context('quote:id') quoteId?: QuoteStore['id']

  /**
   * Collects all `imposesQuoteRequirement` values (`store:field`) from steps in
   * the active flow. Surfaced to resolvers via `requirementsFor` so each resolver
   * can filter to only its own store's requirements.
   */
  get activeQuoteRequirements(): Set<PrefixedStoreField> {
    const steps = this.flowManager.activeFlow?.steps ?? []
    const requirements = steps
      .map((s) => s.imposesQuoteRequirement)
      .filter((r): r is PrefixedStoreField => r !== undefined)

    return new Set(requirements)
  }

  /**
   * Whether the current composed input passes validation
   */
  get isValid() {
    return this._isValid && this._input !== null && this._input !== undefined
  }

  /**
   * The composed quote input.
   */
  get input() {
    return this._input
  }

  /**
   * Whether the current input differs from the last committed quote
   *
   * Checks if the quote input is valid before checking if it's dirty.
   */
  get isDirty(): boolean {
    return this._previousHash !== this._currentHash
  }

  /**
   * Hash of the current input. Cached to avoid re-computing on every access.
   */
  private get _currentHash() {
    return hash(this._input)
  }

  /**
   * Resets the internal data for one or more fields.
   *
   * This is useful when you want to re-resolve a field
   * (e.g., 'customer') based on updated data.
   *
   * Passing no arguments will reset all fields.
   *
   * @param fields The fields to reset
   */
  resetFields<K extends IncludedKeys>(..._fields: K[]): void
  resetFields(...fields: ResolverKey[]): QuoteBuilderService<Mode, ResolverKey> {
    if (fields.length === 0) {
      this._data = {}
      return this
    }

    for (const field of fields) {
      delete this._data[field]
    }

    return this
  }

  /**
   * Switches the quote type mode for subsequent validation.
   * Automatically resets to 'strict' after `compose()` completes, so this only
   * applies to the current chain — it does not leak into future chains.
   * @param quoteType 'strict' or 'sparse'
   */
  type<M extends QuoteType>(quoteType: M): QuoteBuilderService<M, IncludedKeys> {
    this._quoteType = quoteType
    return this
  }

  /**
   * Resolves one or more fields into concrete data and includes the result
   * in the builder’s internal state.
   *
   * Fields may pull data from a store, reference other resolvers,
   * or derive values from previously resolved fields. Traits can optionally
   * be used to modify the resolved value.
   *
   * This method builds up the final quote data reservoir by composing
   * fields into a flat, serializable shape.
   *
   * @param args Either:
   *  - A list of field names to resolve (`'tickets'`, `'availability'`, etc)
   *  - A single field name with a list of trait names to modify its resolved value
   *
   * @example
   * quote.include('tickets', 'availabilityTimeId')
   * quote.include('tickets', ['withQuestionAnswers'])
   */
  include<K extends readonly ResolverKey[]>(..._fields: K): QuoteBuilderService<Mode, IncludedKeys | K[number]>
  include<K extends ResolverKey>(_field: K, _traits: TraitList): QuoteBuilderService<Mode, IncludedKeys | K>
  include<K extends ResolverKey>(...args: [K, TraitList] | K[]): QuoteBuilderService<Mode, IncludedKeys | K> {
    const [first, second] = args

    if (this._isTraitList(second)) {
      this._data[first] = this._resolveField(first, second)
    } else {
      for (const field of args as readonly K[]) {
        this._data[field] = this._resolveField(field)
      }
    }

    return this
  }

  /**
   * Overrides a field's value directly without resolving it.
   * This is useful for bypassing the normal resolution process
   * when you want to set a field's value manually.
   *
   * @param field The field to override
   * @param override The new value for the field
   */
  includeOverride<K extends ResolverKey>(field: K, override: any): QuoteBuilderService<Mode, IncludedKeys | K> {
    this._data[field] = override
    return this
  }

  /**
   * Uses the included data to build full quote inputs
   * by composing and validating based on models and the quote schema.
   *
   * @param fields Field composers to run (e.g., 'bookingQuotes', 'customer')
   */
  compose<const Keys extends ModelKeyWithRefid[]>(...fields: Keys): QuoteBuilderService<Mode, IncludedKeys> {
    let result: QuoteSparseInput = {}

    // merge in any globally configured models that should always be composed
    const alwaysCompose = this._alwaysComposeModels()
    const merged: ModelKeyWithRefid[] = [
      ...new Set([...(fields as string[]), ...alwaysCompose])
    ] as string[] as ModelKeyWithRefid[]

    try {
      // Sparse can only patch an existing quote — there is no such thing as a sparse create.
      if (this._quoteType === 'sparse' && !this.quoteId) {
        this._isValid = false
        return this
      }

      for (const field of merged) {
        const [key, refidAlias] = field.split(':') as [ModelKey, string]
        const refid = (refids[refidAlias ?? field] ||= uid()) // get or create refid for model using provided alias or field name
        let fieldData = this._composeModel(key, refid)

        if (!this._validateModel(key, fieldData)) {
          this._isValid = false
          return this
        }

        result[key] = this._normalizeComposedModel(key, fieldData)
      }

      this._input = result
      this._isValid = true
      return this
    } finally {
      // Reset so a previous .type('sparse') call cannot leak into the next builder chain
      this._quoteType = DEFAULT_QUOTE_TYPE
    }
  }

  /**
   * Sends the built quote input to the backend via `createOrUpdateQuote`.
   * Hashes the current input for dirty-state detection.
   * Logs the quote input for debugging.
   *
   * @returns A promise that resolves when the quote is committed
   */
  async commit(): Promise<void> {
    // Queue only the latest input, we don't need to keep the history
    this._queuedInput = this._input

    // If a request is in flight, let it finish — the queued input will be processed after
    if (this._pending) return

    while (this._queuedInput) {
      const nextInput = this._queuedInput
      this._queuedInput = undefined
      this._pending = true

      // no catch block because we want to surface errors to the caller
      // but we still want to ensure clearing the pending flag
      try {
        await this.createOrUpdateQuote(nextInput as QuoteStrictInput)
        this._log.push(nextInput)
      } finally {
        this._pending = false
      }
    }
  }

  /**
   * Tries to commit the quote if it's valid and dirty. Catches and logs errors.
   */
  async tryCommit() {
    if (this.isDirty && this.isValid) {
      try {
        await this.commit()
        Logger.info('QuoteBuilder: Quote committed successfully.')
      } catch (error) {
        Logger.error('QuoteBuilder: Error committing quote.', { error })
      }
    }
  }

  /**
   * Resets the builder so that it won't apply diffs from previous commits.
   *
   * You probably shouldn't use this. It's really only useful for testing.
   */
  reset(): QuoteBuilderService<Mode, IncludedKeys> {
    refids = {}
    this._pending = false
    this._queuedInput = undefined
    this._log = []

    return this
  }

  private _isTraitList(val: unknown): val is TraitList {
    return Array.isArray(val) && val.every((v) => typeof v === 'string')
  }

  /**
   * FIELD RESOLUTION HELPERS
   */
  private _getResolver<T>(key: ResolverKey): Resolver<T> {
    const resolverConstructor = quoteConfig.resolvers[key]

    if (!resolverConstructor) {
      throw new Error(`No resolver found for field: ${key}`)
    }

    return resolverConstructor({
      resolveStoreProp: <S extends keyof StoreRegistry, K extends StoreFieldKey<S>>(storeName: S, propertyKey: K) =>
        upstreamResolveStoreProp(this, storeName, propertyKey),
      resolveField: this._resolveField.bind(this),
      requirementsFor: <S extends keyof StoreRegistry>(store: S): StoreFieldKey<S>[] =>
        [...this.activeQuoteRequirements]
          .filter((r) => r.startsWith(`${store}:`))
          .map((r) => r.split(':')[1] as StoreFieldKey<S>)
    }) as unknown as Resolver<T>
  }

  private _resolveField(field: ResolverKey, traits: string[] = []): any {
    const { resolve, traits: fieldTraits } = this._getResolver(field)

    let resolvedField = resolve?.()

    for (const trait of traits) {
      if (fieldTraits?.[trait]) {
        resolvedField = fieldTraits[trait](resolvedField)
      } else {
        Logger.warn(`No trait found for field: ${field} with key: ${trait}`)
      }
    }

    return resolvedField
  }

  /**
   * MODEL COMPOSITION HELPERS
   */
  private _composeModel<K extends ModelKey>(key: K, refid: string) {
    const model = this._getModel(key)
    const out: Partial<Record<ResolverKey | 'refid', unknown>> = {}

    for (const k in model) {
      const node = model[k] as SchemaNode | undefined
      const resolverKey = k as ResolverKey | 'refid'

      if (!node) continue

      if (resolverKey === 'refid') {
        out[resolverKey] = refid
      } else if (node.kind === 'attribute') {
        out[resolverKey] = this._data[resolverKey]
      } else if (node.kind === 'resolvesTo') {
        // auto-resolve missing resolver-backed fields so models can be composed
        // even when callers did not explicitly include() them
        out[resolverKey] = this._data[resolverKey] ?? this._resolveField(resolverKey)
      }
    }

    return out
  }

  private _getModel<T extends ModelKey>(key: T): QuoteModel<T> {
    const model = quoteConfig.models[key]

    if (!model) {
      throw new Error(`No model found for key: ${key}`)
    }

    return model as unknown as QuoteModel<T>
  }

  private _validateModel<K extends ModelKey>(key: K, fieldData: any) {
    const model = quoteConfig.models[key]

    for (const k in model) {
      const node = model[k] as SchemaNode | undefined

      if (!node) continue

      if (
        ((node.validate === 'strict' || node.validate === 'sparse') && this._quoteType === 'strict') ||
        (node.validate === 'sparse' && this._quoteType === 'sparse')
      ) {
        if (Array.isArray(fieldData[k]) && fieldData[k].length === 0) return false
        if (fieldData[k] === undefined) return false
      }
    }

    return true
  }

  private _normalizeComposedModel(key: ModelKey, fieldData: any) {
    let result
    const kind = this._getSchemaType(key)

    if (kind === 'hasMany') {
      result = [fieldData]
    } else if (kind === 'belongsTo') {
      result = fieldData[key]
    }

    return result
  }

  private _getSchemaType(key: ModelKey) {
    let kind = quoteConfig.schema[key]?.kind

    if (!kind) {
      for (const k in quoteConfig.schema) {
        if (quoteConfig.schema[k as ModelKey]?.model === key) {
          kind = quoteConfig.schema[k as ModelKey].kind
          break
        }
      }
    }

    return kind
  }

  /**
   * models that should always be composed with every request
   * can be configured via quoteConfig.schema (optional)
   * kept as a method so tests can stub it without mutating the config module
   */
  private _alwaysComposeModels(): ModelKey[] {
    return Object.entries(quoteConfig.schema ?? {})
      .filter(([_k, node]) => node.composePolicy === 'always')
      .map(([k]) => k as ModelKey)
  }
}

declare global {
  export interface QuoteBuilder extends QuoteBuilderService {}
}
